import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Button, Typography, Paper, CircularProgress, TextField, Switch, FormControlLabel } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { AgGridReact } from 'ag-grid-react';
import { getScheduleByProjectId, saveProjectSchedule, PlannedScheduleDTO } from '../api/scheduleApi';
import { getProjectActivities } from '../api/activityApi';
import { ProjectActivity } from '../types/ActivityTypes';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { useSnackbar } from '../contexts/SnackbarContext';

ModuleRegistry.registerModules([AllCommunityModule]);

interface GridRowData {
  activityId: number;
  activityName: string;
  cantidadTorres?: number | null;
  faltantes?: number | null;
  metaSemanal?: number | null;
  eficiencia?: number | null;
  holgura?: number | null;
  ritmoReal?: number | null;
  frente: number | string;
  tipoFila: string;
  projectedWeeks?: string[];
  isCompletado?: boolean;
  autoFilledWeeks?: string[];
  acumulados?: Record<string, any>;
  isRemovedFront?: boolean;
  isActiveFront?: boolean;
  [weekId: string]: any;
}

interface ProjectScheduleProps {
  projectId: number;
}

interface WeekRange {
  id: string;
  headerName: string;
  dates: string[];
  startDate: string;
}

const ProjectSchedule: React.FC<ProjectScheduleProps> = ({ projectId }) => {
  const gridRef = useRef<AgGridReact>(null);
  const anchorRowRef = useRef<{ activityId: number; tipoFila: string } | null>(null);

  const [activities, setActivities] = useState<ProjectActivity[]>([]);
  const [existingSchedule, setExistingSchedule] = useState<PlannedScheduleDTO[]>([]);
  const [rowData, setRowData] = useState<GridRowData[]>([]);
  const [columnDefs, setColumnDefs] = useState<any[]>([]);
  const [weeksData, setWeeksData] = useState<WeekRange[]>([]);

  const [frontsConfig, setFrontsConfig] = useState<Record<number, number>>({});
  const [removedFronts, setRemovedFronts] = useState<Record<number, number[]>>({});
  const [metaOverrides, setMetaOverrides] = useState<Record<number, number>>({});

  const [eficienciaOverrides, setEficienciaOverrides] = useState<Record<number, number>>({});
  const [holguraOverrides, setHolguraOverrides] = useState<Record<number, number>>({});

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showOnlyWithProgress, setShowOnlyWithProgress] = useState<boolean>(false);

  const { showMessage } = useSnackbar();

  useEffect(() => {
    if (projectId) {
      loadInitialData();
    }
  }, [projectId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [scheduleRes, activitiesRes] = await Promise.all([
        getScheduleByProjectId(projectId),
        getProjectActivities(projectId)
      ]);

      const scheduleData: PlannedScheduleDTO[] = scheduleRes.data;
      const actsData: ProjectActivity[] = activitiesRes;

      const initialFronts: Record<number, number> = {};
      actsData.forEach(act => {
        const baseActId = act.activity?.id || 0;
        const records = scheduleData.filter(s => s.activityId === baseActId);
        let maxFront = 1;
        records.forEach((r: any) => {
          if (r.frontNumber && r.frontNumber > maxFront) {
            maxFront = r.frontNumber;
          }
        });
        initialFronts[baseActId] = maxFront;
      });
      setFrontsConfig(initialFronts);
      setRemovedFronts({});
      setExistingSchedule(scheduleData);
      setActivities(actsData);

      let minDate = '9999-12-31';
      let maxDate = new Date().toISOString().split('T')[0];

      let globalLastRealDateStr = '';
      scheduleData.forEach(dto => {
        if (dto.plannedTimeline) {
          Object.keys(dto.plannedTimeline).forEach(date => {
            if (date < minDate) minDate = date;
          });
        }
        if (dto.realTimeline) {
          Object.keys(dto.realTimeline).forEach(date => {
            if (date < minDate) minDate = date;
            if (date > maxDate) maxDate = date;
            if (Number(dto.realTimeline[date]) > 0 && date > globalLastRealDateStr) {
              globalLastRealDateStr = date;
            }
          });
        }
      });

      if (minDate === '9999-12-31') {
        minDate = new Date().toISOString().split('T')[0];
      }

      const actualToday = new Date();
      actualToday.setHours(0, 0, 0, 0);
      const todayStr = actualToday.toISOString().split('T')[0];

      const projectionBaseDate = globalLastRealDateStr ? new Date(`${globalLastRealDateStr}T00:00:00`) : actualToday;

      actsData.forEach(act => {
        // LÍNEA ELIMINADA AQUÍ: Ya no se filtran las actividades con "NA"
        const baseActId = act.activity?.id || 0;
        const records = scheduleData.filter(s => s.activityId === baseActId);
        const rootAct = act as any;

        const totalTorresActividad = Number(rootAct.totalUnits) || 0;

        let valorP0Diario = Number(rootAct.productionGoal);
        if (!valorP0Diario || valorP0Diario === 0) {
          valorP0Diario = Number(rootAct.performaceUnitGoal) ||
                          Number(rootAct.performanceUnitPo) ||
                          Number((act.activity as any)?.objectiveProduction) || 0;
        }

        const numFronts = initialFronts[baseActId] || 1;

        let calculoSemanal = valorP0Diario * 5.5;
        let produccionObjetivoSemanal = Number((Math.round(calculoSemanal * 100) / 100).toFixed(2));

        let metaGuardada = 0;
        const mainRecord = records.find((r: any) => r.frontNumber === 1) || records[0];
        if (mainRecord?.plannedTimeline) {
            const values = Object.values(mainRecord.plannedTimeline).map(v => Number(v)).filter(v => v > 0);
            if (values.length > 0) metaGuardada = values[0] / numFronts;
        }

        let metaSemanalBase = metaGuardada > 0 ? metaGuardada : produccionObjetivoSemanal;
        let metaSemanalTotal = Math.min(metaSemanalBase * numFronts, totalTorresActividad);

        let sumTotalRealGlobal = 0;
        let lastRealDateAct = '';
        let firstActDateStr = '9999-12-31';

        let sumaRitmosFrentes = 0;
        let frentesConDatos = 0;

        for (let f = 1; f <= numFronts; f++) {
          const existingRecord = records.find((r: any) => r.frontNumber === f);
          let sumTotalRealFrente = 0;
          let entriesFrente = 0;

          if (existingRecord?.realTimeline) {
            Object.entries(existingRecord.realTimeline).forEach(([date, val]) => {
              const numVal = Number(val);
              if (numVal > 0) {
                if (date < firstActDateStr) firstActDateStr = date;
                if (date > lastRealDateAct) lastRealDateAct = date;

                sumTotalRealFrente += numVal;
                sumTotalRealGlobal += numVal;
                entriesFrente++;
              }
            });
          }
          const ritmoDiarioFrente = entriesFrente > 0 ? (sumTotalRealFrente / entriesFrente) : 0;
          if (entriesFrente > 0) {
              sumaRitmosFrentes += (ritmoDiarioFrente * 5.5);
              frentesConDatos++;
          }
        }

        if (firstActDateStr === '9999-12-31') firstActDateStr = todayStr;

        let unidadesRestantesGlobal = totalTorresActividad - sumTotalRealGlobal;
        if (unidadesRestantesGlobal < 0) unidadesRestantesGlobal = 0;

        let ritmoFrenteReal = 0;
        if (frentesConDatos > 0) {
            ritmoFrenteReal = sumaRitmosFrentes / frentesConDatos;
        } else {
            ritmoFrenteReal = metaSemanalBase;
        }

        const ritmoEfectivoParaProyectar = Number((ritmoFrenteReal * numFronts).toFixed(2));

        let semanasFaltantesReal = (ritmoEfectivoParaProyectar > 0 && unidadesRestantesGlobal > 0) ? (unidadesRestantesGlobal / ritmoEfectivoParaProyectar) : 0;
        let semanasFaltantesPlan = (metaSemanalTotal > 0 && unidadesRestantesGlobal > 0) ? (unidadesRestantesGlobal / metaSemanalTotal) : 0;

        let semanasFaltantesMax = Math.max(semanasFaltantesReal, semanasFaltantesPlan);
        if (semanasFaltantesMax > 78) semanasFaltantesMax = 78;

        if (semanasFaltantesMax > 0) {
          let baseStartDateForProjection = new Date(projectionBaseDate);
          if (lastRealDateAct && lastRealDateAct > todayStr) {
            baseStartDateForProjection = new Date(`${lastRealDateAct}T00:00:00`);
          }
          baseStartDateForProjection.setDate(baseStartDateForProjection.getDate() + (Math.ceil(semanasFaltantesMax) * 7));
          const projectedEndStr = baseStartDateForProjection.toISOString().split('T')[0];
          if (projectedEndStr > maxDate) maxDate = projectedEndStr;
        }

        if (metaSemanalTotal > 0) {
          let delayDays = 0;
          records.forEach((rec: any) => {
            if (rec.plannedTimeline) {
              Object.values(rec.plannedTimeline).forEach(v => {
                if (Number(v) === 0) delayDays += 7;
              });
            }
          });

          const semanasPlanificadasTotales = Math.ceil(totalTorresActividad / metaSemanalTotal);
          const planEndDate = new Date(`${minDate}T00:00:00`);
          planEndDate.setDate(planEndDate.getDate() + ((semanasPlanificadasTotales + 2) * 7) + delayDays);
          const planEndStr = planEndDate.toISOString().split('T')[0];
          if (planEndStr > maxDate) maxDate = planEndStr;
        }
      });

      setStartDate(minDate);
      setEndDate(maxDate);

    } catch (error) {
      console.error("Error al cargar la base del cronograma:", error);
      showMessage("Error al cargar la información del proyecto.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && activities.length > 0) {
      buildGrid();
    }
  }, [startDate, endDate, activities, existingSchedule, loading, showOnlyWithProgress, frontsConfig, removedFronts, metaOverrides, eficienciaOverrides, holguraOverrides]);

  const saveAnchorRow = (activityId: number, tipoFila: string = 'Real (Total)') => {
    anchorRowRef.current = { activityId, tipoFila };
  };

  const restoreAnchorRow = () => {
    if (!gridRef.current?.api || !anchorRowRef.current) return;

    const { activityId, tipoFila } = anchorRowRef.current;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        let targetIndex: number | null = null;

        gridRef.current!.api.forEachNode((node) => {
          const row = node.data;
          if (
            row &&
            row.activityId === activityId &&
            row.tipoFila === tipoFila
          ) {
            targetIndex = node.rowIndex ?? null;
          }
        });

        if (targetIndex !== null) {
          gridRef.current!.api.ensureIndexVisible(targetIndex, 'middle');
        }
      });
    });
  };

  const frontHasRealProgress = (activityId: number, frontNumber: number) => {
    const record = existingSchedule.find(
      (rec: any) =>
        rec.activityId === activityId &&
        rec.frontNumber === frontNumber
    );

    if (!record?.realTimeline) return false;

    return Object.values(record.realTimeline).some(
      (val: any) => Number(val) > 0
    );
  };

  const gridContext = useMemo(() => ({
    handleAddFront: (activityId: number) => {
      saveAnchorRow(activityId, 'Planificado');
      const current = frontsConfig[activityId] || 1;
      const next = current + 1;

      setRemovedFronts(prevRemoved => {
        const removedForActivity = prevRemoved[activityId] || [];

        if (removedForActivity.includes(next)) {
          return {
            ...prevRemoved,
            [activityId]: removedForActivity.filter(f => f !== next)
          };
        }

        return prevRemoved;
      });

      setFrontsConfig(prev => ({
        ...prev,
        [activityId]: next
      }));
    },

    handleRemoveFront: (activityId: number) => {
      saveAnchorRow(activityId, 'Planificado');
      const current = frontsConfig[activityId] || 1;
      if (current <= 1) return;

      const frontToRemove = current;
      const hasProgress = frontHasRealProgress(activityId, frontToRemove);

      if (hasProgress) {
        setRemovedFronts(prevRemoved => {
          const removedForActivity = prevRemoved[activityId] || [];
          if (removedForActivity.includes(frontToRemove)) return prevRemoved;

          return {
            ...prevRemoved,
            [activityId]: [...removedForActivity, frontToRemove]
          };
        });
      } else {
        setRemovedFronts(prevRemoved => {
          const removedForActivity = prevRemoved[activityId] || [];
          if (!removedForActivity.includes(frontToRemove)) return prevRemoved;

          return {
            ...prevRemoved,
            [activityId]: removedForActivity.filter(f => f !== frontToRemove)
          };
        });
      }

      setFrontsConfig(prev => ({
        ...prev,
        [activityId]: current - 1
      }));
    }
  }), [frontsConfig, existingSchedule]);

  const getWeeksBetween = (start: string, end: string): WeekRange[] => {
    const weeks: WeekRange[] = [];
    if (!start || !end) return weeks;

    let current = new Date(`${start}T00:00:00`);
    const stop = new Date(`${end}T00:00:00`);

    if (isNaN(current.getTime()) || isNaN(stop.getTime()) || current > stop) return [];

    const day = current.getDay();
    const diff = current.getDate() - day + (day === 0 ? -6 : 1);
    let weekStart = new Date(current.setDate(diff));

    let weekIndex = 1;
    while (weekStart <= stop) {
      let weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const datesInWeek: string[] = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        datesInWeek.push(d.toISOString().split('T')[0]);
      }

      const fmtStart = weekStart.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' });
      const fmtEnd = weekEnd.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' });

      weeks.push({
        id: `week_${weekIndex}_${datesInWeek[0]}`,
        headerName: `Sem ${weekIndex}\n(${fmtStart} - ${fmtEnd})`,
        dates: datesInWeek,
        startDate: datesInWeek[0]
      });

      weekStart.setDate(weekStart.getDate() + 7);
      weekIndex++;
    }
    return weeks;
  };

  const handleCellValueChanged = (params: any) => {
    if (params.data.tipoFila === 'Planificado') {
      const field = params.colDef.field;
      const rawValue = params.newValue;
      const newValue = parseFloat(rawValue);

      if (field === 'metaSemanal' && !isNaN(newValue) && newValue >= 0) {
        setMetaOverrides(prev => ({ ...prev, [params.data.activityId]: newValue }));
        setExistingSchedule(prev => {
          return prev.map((rec: any) => {
            if (rec.activityId === params.data.activityId && rec.frontNumber === 1) {
              const newRec = { ...rec, plannedTimeline: { ...(rec.plannedTimeline || {}) } };
              const activeFronts = params.data.frente || 1;
              const newTotal = newValue * activeFronts;
              Object.keys(newRec.plannedTimeline).forEach(d => {
                if (newRec.plannedTimeline[d] > 0) {
                  newRec.plannedTimeline[d] = newTotal;
                }
              });
              return newRec;
            }
            return rec;
          });
        });
      }
      else if (field === 'eficiencia' && !isNaN(newValue) && newValue >= 0) {
        setEficienciaOverrides(prev => ({ ...prev, [params.data.activityId]: newValue }));
      }
      else if (field === 'holgura' && !isNaN(newValue) && newValue >= 0) {
        setHolguraOverrides(prev => ({ ...prev, [params.data.activityId]: newValue }));
      }
      else if (field.startsWith('week_')) {
        const week = weeksData.find(w => w.id === field);
        if (week) {
          const isAuto = rawValue === '' || rawValue === null || rawValue === undefined || (typeof rawValue === 'number' && isNaN(rawValue));

          setExistingSchedule(prev => {
            const exists = prev.some((r: any) => r.activityId === params.data.activityId && r.frontNumber === 1);
            let newState = prev.map((rec: any) => {
              if (rec.activityId === params.data.activityId && rec.frontNumber === 1) {
                const newRec = { ...rec, plannedTimeline: { ...(rec.plannedTimeline || {}) } };
                if (isAuto) {
                  delete newRec.plannedTimeline[week.startDate];
                } else if (!isNaN(newValue) && newValue >= 0) {
                  newRec.plannedTimeline[week.startDate] = newValue;
                }
                return newRec;
              }
              return rec;
            });

            if (!exists && !isAuto && !isNaN(newValue) && newValue >= 0) {
              newState.push({
                activityId: params.data.activityId,
                frontNumber: 1,
                plannedTimeline: { [week.startDate]: newValue },
                realTimeline: {}
              } as any);
            }
            return newState;
          });
        }
      }
    }
  };

  const handleCellClicked = (params: any) => {
    if (params.data && params.data.tipoFila === 'Planificado' && params.colDef.field.startsWith('week_')) {
      const metaBase = params.data.metaSemanal;
      const activeFronts = params.data.frente;
      const metaTotal = (metaBase || 0) * (Number(activeFronts) || 1);
      if (!metaTotal || metaTotal <= 0) return;

      const currentValue = Number(params.value) || 0;
      const newValue = currentValue > 0 ? 0 : metaTotal;

      params.node.setDataValue(params.colDef.field, newValue);
    }
  };

  const safeNumberFormatter = (params: any) => {
    if (params.value === 0 || params.value === '0') return '0';
    if (params.value === null || params.value === undefined || params.value === '') return '';
    const num = Number(params.value);
    return isNaN(num) ? '' : num.toFixed(2).replace(/\.00$/, '');
  };

  const buildGrid = () => {
    const weeks = getWeeksBetween(startDate, endDate);
    setWeeksData(weeks);

    const cols: any[] = [
      { field: 'activityName', headerName: 'Actividad', pinned: 'left', width: 230, editable: false, tooltipField: 'activityName' },
      {
        field: 'cantidadTorres',
        headerName: 'Torres / U.',
        pinned: 'left',
        width: 100,
        editable: false,
        cellRenderer: (params: any) => {
          if (params.data.tipoFila === 'Planificado' && params.data.isCompletado) {
            return (
              <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                {safeNumberFormatter(params)} ✅
              </span>
            );
          }
          return safeNumberFormatter(params);
        },
        cellStyle: (params: any) => {
          if (params.data.tipoFila === 'Planificado') return { fontWeight: 'bold', fontSize: '12px', textAlign: 'center', backgroundColor: params.data.isCompletado ? '#e8f5e9' : '#f9fbe7' };
          if (params.data.tipoFila === 'Real (Total)') return { fontWeight: 'bold', fontSize: '12px', textAlign: 'center', backgroundColor: '#fafafa', color: '#424242' };
          return { fontSize: '12px', textAlign: 'center', color: '#616161' };
        }
      },
      {
        field: 'faltantes',
        headerName: 'Faltantes',
        pinned: 'left',
        width: 85,
        editable: false,
        valueFormatter: safeNumberFormatter,
        cellStyle: (params: any) => {
          if (params.data.tipoFila === 'Planificado') return { backgroundColor: params.data.isCompletado ? '#e8f5e9' : '#f9fbe7', fontSize: '12px', textAlign: 'center' };
          if (params.data.tipoFila === 'Real (Total)') return { fontWeight: 'bold', fontSize: '12px', textAlign: 'center', backgroundColor: '#ffebee', color: '#c62828' };
          return { fontSize: '12px', textAlign: 'center', color: '#616161' };
        }
      },
      {
        field: 'metaSemanal',
        headerName: 'Meta Sem.',
        pinned: 'left',
        width: 90,
        editable: (params: any) => params.data.tipoFila === 'Planificado',
        valueParser: (params: any) => parseFloat(params.newValue),
        valueFormatter: safeNumberFormatter,
        cellStyle: (params: any) => {
          if (params.data.tipoFila === 'Planificado') return { fontWeight: 'bold', color: '#1565c0', textAlign: 'center', backgroundColor: '#e3f2fd', cursor: 'text', border: '1px dashed #90caf9', fontSize: '12px' };
          return { backgroundColor: 'transparent', fontSize: '12px' };
        }
      },
      {
        field: 'eficiencia',
        headerName: 'Efic. (%)',
        pinned: 'left',
        width: 80,
        editable: (params: any) => params.data.tipoFila === 'Planificado',
        valueParser: (params: any) => parseFloat(params.newValue),
        valueFormatter: (params: any) => {
          if (params.data.tipoFila === 'Planificado') {
            return (params.value !== null && params.value !== undefined && params.value !== '') ? `${params.value}%` : '0%';
          }
          return '';
        },
        cellStyle: (params: any) => {
          if (params.data.tipoFila === 'Planificado') return { fontWeight: 'bold', color: '#00695c', textAlign: 'center', backgroundColor: '#e0f2f1', cursor: 'text', border: '1px dashed #80cbc4', fontSize: '12px' };
          return { backgroundColor: 'transparent', fontSize: '12px' };
        }
      },
      {
        field: 'holgura',
        headerName: 'Holgura (U)',
        pinned: 'left',
        width: 85,
        editable: (params: any) => params.data.tipoFila === 'Planificado',
        valueParser: (params: any) => parseFloat(params.newValue),
        valueFormatter: safeNumberFormatter,
        cellStyle: (params: any) => {
          if (params.data.tipoFila === 'Planificado') return { fontWeight: 'bold', color: '#6a1b9a', textAlign: 'center', backgroundColor: '#f3e5f5', cursor: 'text', border: '1px dashed #ce93d8', fontSize: '12px' };
          return { backgroundColor: 'transparent', fontSize: '12px' };
        }
      },
      {
        field: 'ritmoReal',
        headerName: 'Ritmo Real',
        pinned: 'left',
        width: 90,
        editable: false,
        valueFormatter: safeNumberFormatter,
        cellStyle: (params: any) => {
          if ((params.data.tipoFila === 'Real' || params.data.tipoFila === 'Real (Total)') && params.value) return { fontWeight: 'bold', color: '#e65100', textAlign: 'center', fontSize: '12px' };
          return { backgroundColor: 'transparent', fontSize: '12px' };
        }
      },
      {
        field: 'frente',
        headerName: 'Frentes',
        pinned: 'left',
        width: 105,
        editable: false,
        cellRenderer: (params: any) => {
          if (params.data.tipoFila === 'Planificado') {
            const numFronts = params.value;
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', paddingRight: '2px', fontSize: '12px' }}>
                <span style={{ fontWeight: 'bold', color: '#1565c0' }}>{numFronts} Ftes</span>
                <div style={{ display: 'flex', gap: '2px' }}>
                  <button onClick={() => params.context.handleAddFront(params.data.activityId)} style={{ cursor: 'pointer', width: '20px', height: '20px', borderRadius: '4px', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', color: '#1565c0', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Añadir Frente">+</button>
                  {numFronts > 1 && <button onClick={() => params.context.handleRemoveFront(params.data.activityId)} style={{ cursor: 'pointer', width: '20px', height: '20px', borderRadius: '4px', backgroundColor: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Quitar Frente">-</button>}
                </div>
              </div>
            );
          } else if (params.data.tipoFila === 'Real (Total)') {
            return <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#424242' }}>Total</span>;
          } else {
            return (
              <span style={{ fontSize: '12px', color: params.data.isRemovedFront ? '#9e9e9e' : '#757575', fontStyle: params.data.isRemovedFront ? 'italic' : 'normal' }}>
                Fte {params.value}{params.data.isRemovedFront ? ' (Quitado)' : ''}
              </span>
            );
          }
        }
      },
      { field: 'tipoFila', headerName: 'Tipo', pinned: 'left', width: 95, editable: false, cellStyle: { fontSize: '12px' } }
    ];

    weeks.forEach(week => {
      cols.push({
        field: week.id,
        headerName: week.headerName,
        width: 95,
        editable: (params: any) => params.data.tipoFila === 'Planificado',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: (params: any) => {
          const metaBase = params.data.metaSemanal;
          const activeFronts = params.data.frente;
          const metaTotal = (metaBase || 0) * (Number(activeFronts) || 1);
          const options = ['0'];
          if (metaTotal > 0) {
            options.push(String(metaTotal));
          }
          return { values: options };
        },
        valueParser: (params: any) => {
          if (params.newValue === '' || params.newValue === null) return '';
          return parseFloat(params.newValue);
        },
        cellRenderer: (params: any) => {
          const val = safeNumberFormatter(params);
          const isPlanificado = params.data.tipoFila === 'Planificado';
          const isRealTotal = params.data.tipoFila === 'Real (Total)';

          if ((isPlanificado || isRealTotal) && params.data.acumulados) {
            const acumulado = params.data.acumulados[params.colDef.field];
            if (acumulado !== undefined && acumulado !== '') {
              const isProj = params.data.projectedWeeks?.includes(params.colDef.field);
              const colorAcum = isPlanificado ? '#78909c' : (isProj ? '#ef5350' : '#f57c00');

              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: '1.2', marginTop: '4px' }}>
                  <span>{val !== '' ? val : '-'}</span>
                  <span style={{ fontSize: '10px', color: colorAcum, fontWeight: 'normal', fontStyle: isProj ? 'italic' : 'normal' }}>
                    Acum: {acumulado}
                  </span>
                </div>
              );
            }
          }
          return val;
        },
        cellStyle: (params: any) => {
          if (params.data.tipoFila === 'Planificado') {
            const isAuto = params.data.autoFilledWeeks?.includes(params.colDef.field);
            return {
              backgroundColor: '#e3f2fd',
              fontWeight: isAuto ? 'normal' : 'bold',
              color: isAuto ? '#546e7a' : (params.value === 0 ? '#d32f2f' : '#1565c0'),
              fontStyle: isAuto ? 'italic' : 'normal',
              fontSize: '12px',
              padding: '0 4px',
              border: isAuto ? '1px dotted #90caf9' : '1px solid #64b5f6',
              cursor: 'pointer',
              textAlign: 'center'
            };
          }

          const isProj = params.data.projectedWeeks?.includes(params.colDef.field);

          if (params.data.tipoFila === 'Real (Total)') {
            return {
              backgroundColor: isProj ? '#ffebee' : '#fafafa',
              color: isProj ? '#d32f2f' : '#ef6c00',
              fontStyle: isProj ? 'italic' : 'normal',
              fontSize: '12px',
              padding: '0 4px',
              fontWeight: 'bold',
              textAlign: 'center'
            };
          }

          if (params.data.tipoFila === 'Real') {
            if (params.data.isRemovedFront) {
              return {
                backgroundColor: '#eeeeee',
                color: '#757575',
                fontStyle: 'italic',
                fontSize: '12px',
                padding: '0 4px',
                textAlign: 'center'
              };
            }

            if (isProj) {
              return {
                backgroundColor: '#fff5f5',
                color: '#d32f2f',
                fontStyle: 'italic',
                fontSize: '12px',
                padding: '0 4px',
                fontWeight: 'bold',
                textAlign: 'center'
              };
            }
          }

          if (params.value === 0 || params.value > 0) return { backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '12px', padding: '0 4px', textAlign: 'center' };
          return { fontSize: '12px', padding: '0 4px', textAlign: 'center' };
        }
      });
    });
    setColumnDefs(cols);

    // LÍNEA MODIFICADA AQUÍ: Se toma el arreglo completo en lugar de filtrar
    let actividadesProductivas = [...activities];

    let globalLastRealDateStr = '';
    existingSchedule.forEach(dto => {
      if (dto.realTimeline) {
        Object.entries(dto.realTimeline).forEach(([date, val]) => {
          if (Number(val) > 0 && date > globalLastRealDateStr) {
            globalLastRealDateStr = date;
          }
        });
      }
    });

    const actualToday = new Date();
    actualToday.setHours(0, 0, 0, 0);
    const projectionBaseDate = globalLastRealDateStr ? new Date(`${globalLastRealDateStr}T00:00:00`) : actualToday;

    let baseWeekIdx = -1;
    if (globalLastRealDateStr) {
      baseWeekIdx = weeks.findIndex(w => w.dates.includes(globalLastRealDateStr));
    }
    if (baseWeekIdx === -1) {
      baseWeekIdx = weeks.findIndex(w => new Date(`${w.dates[w.dates.length - 1]}T00:00:00`) >= projectionBaseDate);
    }
    const safeCurrentWeekIdx = baseWeekIdx !== -1 ? baseWeekIdx : weeks.length;
    const projectionStartIndex = globalLastRealDateStr !== '' ? safeCurrentWeekIdx + 1 : 0;

    const firstVisibleDate = weeks.length > 0 ? weeks[0].startDate : '9999-12-31';

    const acumuladosRealGlobales: Record<number, Record<string, number>> = {};
    const acumuladosPlanGlobales: Record<number, Record<string, number>> = {};

    const actividadesOrdenadas = [...actividadesProductivas].sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

    const flatData: GridRowData[] = actividadesOrdenadas.flatMap(act => {
      const rootAct = act as any;
      const baseActivityId = act.activity?.id || 0;
      const depId = rootAct.activityIdDependency;

      const existingRecords = existingSchedule.filter(s => s.activityId === baseActivityId);
      const totalTorresActividad = Number(rootAct.totalUnits) || 0;

      let valorP0Diario = Number(rootAct.productionGoal);
      if (!valorP0Diario || valorP0Diario === 0) {
        valorP0Diario = Number(rootAct.performaceUnitGoal) ||
                        Number(rootAct.performanceUnitPo) ||
                        Number((act.activity as any)?.objectiveProduction) || 0;
      }

      const actName = act.activity?.name || 'Sin nombre';
      const subAct = act.activity?.subActivity;
      const unitProd = act.activity?.unitOfMeasurementProduction;
      let unit = (unitProd && unitProd !== 'NA') ? unitProd : (act.activity?.unitOfMeasurement !== 'NA' ? act.activity?.unitOfMeasurement : '');
      let baseName = (subAct && subAct.trim() !== '' && subAct.trim() !== 'NA') ? `${actName} - ${subAct}` : actName;
      const displayName = unit ? `${baseName} (${unit}/sem)` : baseName;

      const activeFrontsCount = frontsConfig[baseActivityId] || 1;
      const removedForActivity = removedFronts[baseActivityId] || [];

      const maxExistingFront = existingRecords.reduce((max, rec: any) => {
        return Math.max(max, rec.frontNumber || 0);
      }, 0);

      const maxRemovedFront = removedForActivity.length > 0
        ? Math.max(...removedForActivity)
        : 0;

      const maxFrontToEvaluate = Math.max(
        activeFrontsCount,
        maxExistingFront,
        maxRemovedFront
      );

      const visibleFrontNumbers: number[] = [];
      for (let f = 1; f <= maxFrontToEvaluate; f++) {
        const removed = removedForActivity.includes(f);
        const hasProgress = frontHasRealProgress(baseActivityId, f);

        if (f <= activeFrontsCount || (removed && hasProgress)) {
          visibleFrontNumbers.push(f);
        }
      }

      const activeFrontNumbers = visibleFrontNumbers.filter(f => !removedForActivity.includes(f));

      let calculoSemanal = valorP0Diario * 5.5;
      let produccionObjetivoSemanal = Number((Math.round(calculoSemanal * 100) / 100).toFixed(2));

      let metaGuardada = 0;
      const mainRecord = existingRecords.find((r: any) => r.frontNumber === 1) || existingRecords[0];
      if (mainRecord?.plannedTimeline) {
          const values = Object.values(mainRecord.plannedTimeline).map(v => Number(v)).filter(v => v > 0);
          if (values.length > 0) metaGuardada = values[0] / activeFrontNumbers.length;
      }

      let metaSemanalBase = metaOverrides[baseActivityId] !== undefined
        ? metaOverrides[baseActivityId]
        : (metaGuardada > 0 ? metaGuardada : produccionObjetivoSemanal);

      let metaSemanalTotal = metaSemanalBase * activeFrontNumbers.length;

      let eficienciaPorcentaje = eficienciaOverrides[baseActivityId] || 0;
      let holguraUnidades = holguraOverrides[baseActivityId] || 0;

      let sumTotalRealGlobal = 0;
      let sumaRitmosFrentes = 0;
      let frentesConDatos = 0;

      let planAcumAntesDelFiltro = 0;
      let realAcumAntesDelFiltro = 0;

      if (mainRecord?.plannedTimeline) {
        Object.entries(mainRecord.plannedTimeline).forEach(([date, val]) => {
          if (date < firstVisibleDate && Number(val) > 0) planAcumAntesDelFiltro += Number(val);
        });
      }

      for (let f = 1; f <= maxFrontToEvaluate; f++) {
        const existingRecord = existingRecords.find((r: any) => r.frontNumber === f);
        let sumTotalRealFrente = 0;
        let entriesFrente = 0;

        if (existingRecord?.realTimeline) {
          Object.entries(existingRecord.realTimeline).forEach(([date, val]) => {
            if (Number(val) > 0) {
              if (date < firstVisibleDate) realAcumAntesDelFiltro += Number(val);
              sumTotalRealFrente += Number(val);
              sumTotalRealGlobal += Number(val);
              entriesFrente++;
            }
          });
        }

        const ritmoDiarioFrente = entriesFrente > 0 ? (sumTotalRealFrente / entriesFrente) : 0;

        if (activeFrontNumbers.includes(f)) {
          if (entriesFrente > 0) {
            sumaRitmosFrentes += (ritmoDiarioFrente * 5.5);
            frentesConDatos++;
          }
        }
      }

      let ritmoFrenteReal = 0;
      if (frentesConDatos > 0) {
          ritmoFrenteReal = sumaRitmosFrentes / frentesConDatos;
      } else {
          ritmoFrenteReal = metaSemanalBase;
      }

      const ritmoEfectivoParaProyectar = Number((ritmoFrenteReal * activeFrontNumbers.length).toFixed(2));

      let unidadesRestantesGlobal = totalTorresActividad - sumTotalRealGlobal;
      if (unidadesRestantesGlobal < 0) unidadesRestantesGlobal = 0;
      const isCompletado = sumTotalRealGlobal >= totalTorresActividad;

      let remainingGlobalParaProyectar = unidadesRestantesGlobal;
      const globalProjectionByWeek: Record<string, number> = {};
      let acumuladoParaProyectarReal = sumTotalRealGlobal;

      weeks.forEach((week, index) => {
        if (index >= projectionStartIndex) {
          if (ritmoEfectivoParaProyectar > 0 && remainingGlobalParaProyectar > 0.001) {

            let Pred_Acumulado_Hoy_Real = totalTorresActividad;
            let Pred_Acumulado_Ayer_Real = totalTorresActividad;

            if (depId && depId > 0 && acumuladosRealGlobales[depId]) {
              Pred_Acumulado_Ayer_Real = (index > 0)
                ? (acumuladosRealGlobales[depId][weeks[index - 1].id] || 0)
                : (acumuladosRealGlobales[depId]['_base'] || 0);

              Pred_Acumulado_Hoy_Real = acumuladosRealGlobales[depId][week.id] || 0;
            }

            let Espacio_Disponible_Real = Math.max(0, Math.min(Pred_Acumulado_Ayer_Real + holguraUnidades, totalTorresActividad) - acumuladoParaProyectarReal);
            let Avance_Teorico = Math.min(ritmoEfectivoParaProyectar, remainingGlobalParaProyectar, Espacio_Disponible_Real);

            let Hay_Bloqueo = (depId && depId > 0 && Pred_Acumulado_Ayer_Real === 0);
            let Final_Obra = false;
            if (depId && depId > 0 && Pred_Acumulado_Hoy_Real >= totalTorresActividad) {
             Final_Obra = true;
            }
            if ((acumuladoParaProyectarReal + Avance_Teorico + 0.01) >= totalTorresActividad) {
             Final_Obra = true;
            }
            let proj = 0;
            if (!Hay_Bloqueo) {
              let umbral = ritmoEfectivoParaProyectar * (eficienciaPorcentaje / 100);
              if ((Avance_Teorico + 0.01) >= umbral || Final_Obra) {
                proj = Avance_Teorico;
              }
            }

            globalProjectionByWeek[week.id] = proj;
            remainingGlobalParaProyectar -= proj;
            acumuladoParaProyectarReal += proj;
          } else {
            globalProjectionByWeek[week.id] = 0;
          }
        }
      });

      const plannedRow: GridRowData = {
        activityId: baseActivityId,
        activityName: displayName,
        cantidadTorres: totalTorresActividad,
        faltantes: null,
        metaSemanal: metaSemanalBase > 0 ? Number(metaSemanalBase.toFixed(2)) : null,
        eficiencia: eficienciaPorcentaje,
        holgura: holguraUnidades,
        ritmoReal: null,
        frente: activeFrontsCount,
        tipoFila: 'Planificado',
        isCompletado: isCompletado,
        autoFilledWeeks: [],
        acumulados: {}
      };

      const realTotalRow: GridRowData = {
        activityId: baseActivityId,
        activityName: '',
        cantidadTorres: sumTotalRealGlobal > 0 ? Number(sumTotalRealGlobal.toFixed(2)) : 0,
        faltantes: unidadesRestantesGlobal > 0 ? Number(unidadesRestantesGlobal.toFixed(2)) : 0,
        metaSemanal: null,
        ritmoReal: ritmoEfectivoParaProyectar > 0 ? Number(ritmoEfectivoParaProyectar.toFixed(2)) : null,
        frente: 'Total',
        tipoFila: 'Real (Total)',
        projectedWeeks: [],
        acumulados: {}
      };

      weeks.forEach(w => { realTotalRow[w.id] = 0; });

      let unidadesPlanificadasAcumuladas = planAcumAntesDelFiltro;
      if (!acumuladosPlanGlobales[baseActivityId]) acumuladosPlanGlobales[baseActivityId] = {};
      acumuladosPlanGlobales[baseActivityId]['_base'] = planAcumAntesDelFiltro;

      weeks.forEach((week, index) => {
        const faltante = totalTorresActividad - unidadesPlanificadasAcumuladas;
        let valToSet: number | string = '';

        const explicitVal = mainRecord?.plannedTimeline && mainRecord.plannedTimeline[week.startDate];

        if (explicitVal !== undefined) {
          const numVal = Number(explicitVal);
          if (numVal === 0) {
            valToSet = 0;
          } else if (numVal > 0 && faltante > 0.001) {
            const planVal = Math.min(numVal, faltante);
            valToSet = Number(planVal.toFixed(2));
            unidadesPlanificadasAcumuladas += planVal;
          } else {
            valToSet = '';
          }
        } else {
          if (faltante > 0.001 && metaSemanalTotal > 0) {

            let Pred_Acumulado_Ayer_Plan = totalTorresActividad;

            if (depId && depId > 0 && acumuladosPlanGlobales[depId]) {
              Pred_Acumulado_Ayer_Plan = (index > 0)
                ? (acumuladosPlanGlobales[depId][weeks[index - 1].id] || 0)
                : (acumuladosPlanGlobales[depId]['_base'] || 0);
            }

            let Espacio_Disponible_Plan = Math.max(0, Pred_Acumulado_Ayer_Plan - unidadesPlanificadasAcumuladas);
            let projPlan = Math.min(metaSemanalTotal, faltante, Espacio_Disponible_Plan);

            if (projPlan > 0) {
              valToSet = Number(projPlan.toFixed(2));
              unidadesPlanificadasAcumuladas += projPlan;
              plannedRow.autoFilledWeeks?.push(week.id);
            } else {
              valToSet = 0;
              plannedRow.autoFilledWeeks?.push(week.id);
            }
          } else {
            valToSet = '';
          }
        }

        plannedRow[week.id] = valToSet;
        acumuladosPlanGlobales[baseActivityId][week.id] = unidadesPlanificadasAcumuladas;
      });

      let planAcum = planAcumAntesDelFiltro;
      weeks.forEach(week => {
        const val = plannedRow[week.id];
        if (val !== '' && val !== null && val !== undefined) {
          planAcum += Number(val);
        }
        plannedRow.acumulados![week.id] = planAcum > 0 ? Number(planAcum.toFixed(2)) : '';
      });

      const frontRows: GridRowData[] = [];

      for (const f of visibleFrontNumbers) {
        const existingRecord = existingRecords.find((r: any) => r.frontNumber === f);
        const isRemovedFrontRow = removedForActivity.includes(f);
        const isActiveFrontRow = !isRemovedFrontRow;

        let sumTotalRealFrenteVista = 0;
        if (existingRecord?.realTimeline) {
          Object.values(existingRecord.realTimeline).forEach(val => {
            if (Number(val) > 0) sumTotalRealFrenteVista += Number(val);
          });
        }

        const realRow: GridRowData = {
          activityId: baseActivityId,
          activityName: '',
          cantidadTorres: sumTotalRealFrenteVista > 0 ? Number(sumTotalRealFrenteVista.toFixed(2)) : 0,
          faltantes: null,
          metaSemanal: null,
          ritmoReal: (ritmoFrenteReal > 0 && isActiveFrontRow)
            ? Number(ritmoFrenteReal.toFixed(2))
            : null,
          frente: f,
          tipoFila: 'Real',
          projectedWeeks: [],
          isRemovedFront: isRemovedFrontRow,
          isActiveFront: isActiveFrontRow
        };

        weeks.forEach((week, index) => {
          let sumReal = 0;

          if (existingRecord?.realTimeline) {
            week.dates.forEach(dateStr => {
              if (existingRecord.realTimeline[dateStr] !== undefined) {
                sumReal += Number(existingRecord.realTimeline[dateStr]);
              }
            });
          }

          if (index < projectionStartIndex) {
            realRow[week.id] = sumReal > 0 ? sumReal : '';
            realTotalRow[week.id] = Number(realTotalRow[week.id]) + sumReal;
          } else {
            const projGlobal = globalProjectionByWeek[week.id] || 0;

            if (projGlobal > 0.001 && isActiveFrontRow && activeFrontNumbers.length > 0) {
              const projFrente = projGlobal / activeFrontNumbers.length;
              realRow[week.id] = Number(projFrente.toFixed(2));
              realRow.projectedWeeks?.push(week.id);

              realTotalRow[week.id] = Number(realTotalRow[week.id]) + projFrente;
              if (!realTotalRow.projectedWeeks?.includes(week.id)) {
                realTotalRow.projectedWeeks?.push(week.id);
              }
            } else {
              realRow[week.id] = '';
            }
          }
        });
        frontRows.push(realRow);
      }

      let realAcum = realAcumAntesDelFiltro;
      if (!acumuladosRealGlobales[baseActivityId]) acumuladosRealGlobales[baseActivityId] = {};
      acumuladosRealGlobales[baseActivityId]['_base'] = realAcumAntesDelFiltro;

      weeks.forEach(w => {
        if (realTotalRow[w.id] === 0) {
          realTotalRow[w.id] = '';
        } else if (realTotalRow[w.id] !== '') {
          realTotalRow[w.id] = Number(Number(realTotalRow[w.id]).toFixed(2));
        }

        const val = realTotalRow[w.id];
        if (val !== '' && val !== null && val !== undefined) {
          realAcum += Number(val);
        }
        realTotalRow.acumulados![w.id] = realAcum > 0 ? Number(realAcum.toFixed(2)) : '';

        acumuladosRealGlobales[baseActivityId][w.id] = realAcum;
      });

      const rows: GridRowData[] = [plannedRow, realTotalRow, ...frontRows];
      if (showOnlyWithProgress) {
        const tieneAvanceReal = existingRecords.some((r: any) =>
          r.realTimeline && Object.values(r.realTimeline).some((val: any) => Number(val) > 0)
        );
        if (!tieneAvanceReal) {
          return [];
        }
      }

      return rows;
    });

    setRowData(flatData);
  };

  useEffect(() => {
    if (rowData.length > 0) {
      restoreAnchorRow();
    }
  }, [rowData]);

  const handleSave = async () => {
    if (!gridRef.current) return;
    setSaving(true);

    try {
      const currentGridData: GridRowData[] = [];
      gridRef.current.api.forEachNode((node) => currentGridData.push(node.data));

      const payload: any[] = currentGridData
        .filter(row => row.tipoFila === 'Planificado')
        .map(row => {
          const plannedTimeline: Record<string, number> = {};

          weeksData.forEach(week => {
            const rawValue = row[week.id];

            if (rawValue !== '' && rawValue !== null && rawValue !== undefined) {
              plannedTimeline[week.startDate] = Number(rawValue);
            }
          });

          return {
            activityId: row.activityId,
            activityName: row.activityName,
            subActivity: null,
            frontNumber: 1,
            plannedTimeline: plannedTimeline,
            realTimeline: {},
            eficiencia: row.eficiencia || 0,
            holgura: row.holgura || 0
          };
        });

      await saveProjectSchedule(projectId, payload as PlannedScheduleDTO[]);
      showMessage("Planificación semanal guardada exitosamente.", "success");

      loadInitialData();
    } catch (error) {
      console.error("Error al guardar la planificación:", error);
      showMessage("Ocurrió un error al guardar la planificación.", "error");
    } finally {
      setSaving(false);
    }
  };

  const getRowId = useMemo(() => {
    return (params: any) => {
      return `${params.data.activityId}_${params.data.tipoFila}_${params.data.frente}`;
    };
  }, []);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: false,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  }), []);

  const getRowStyle = (params: any) => {
    if (params.data.isRemovedFront) {
      return {
        borderBottom: '1px dashed #bdbdbd',
        backgroundColor: '#eeeeee',
        color: '#757575'
      };
    }

    if (params.data.tipoFila === 'Planificado') {
      return {
        borderBottom: '2px solid #90caf9',
        backgroundColor: '#ffffff',
        color: '#000000'
      };
    }

    if (params.data.tipoFila === 'Real (Total)') {
      return {
        borderBottom: '2px solid #e0e0e0',
        backgroundColor: '#fafafa',
        color: '#000000'
      };
    }

    return {
      borderBottom: '1px dashed #f0f0f0',
      backgroundColor: '#ffffff',
      color: '#000000'
    };
  };

  if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar Planificación Semanal'}
        </Button>
      </Box>

      <Paper elevation={1} sx={{ p: 2, display: 'flex', gap: 3, alignItems: 'center', backgroundColor: '#f5f5f5' }}>
        <Typography variant="body1" fontWeight="bold">Rango de Visión:</Typography>
        <TextField
          label="Fecha de Inicio"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Fecha Fin"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />

        <FormControlLabel
          control={
            <Switch
              checked={showOnlyWithProgress}
              onChange={(e) => setShowOnlyWithProgress(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" fontWeight="bold" color="textSecondary">
              Ocultar sin avance
            </Typography>
          }
          sx={{ ml: 2 }}
        />
      </Paper>

      <Paper elevation={3} sx={{ flexGrow: 1, height: '60vh', width: '100%' }}>
        {(startDate && endDate && rowData.length > 0) ? (
          <div className="ag-theme-material" style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              context={gridContext}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              stopEditingWhenCellsLoseFocus={true}
              animateRows={true}
              onCellValueChanged={handleCellValueChanged}
              onCellClicked={handleCellClicked}
              rowHeight={45}
              getRowStyle={getRowStyle}
              headerHeight={50}
            />
          </div>
        ) : (
          <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              Selecciona una Fecha de Inicio y Fecha Fin para mostrar el panel interactivo semanal.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ProjectSchedule;